import { z } from 'zod';
import { ACTION_STATUSES } from './actions';
import { isoDaySchema as isoDate } from './dates';
import { paginatedSchema, paginationQuerySchema } from './pagination';

/** Status da iniciativa estratégica (T-090, PRD §11.18; DEC-040). */
export const INITIATIVE_STATUSES = [
  'PLANNED',
  'IN_PROGRESS',
  'AT_RISK',
  'DONE',
  'SUSPENDED',
  'CANCELLED',
] as const;
export type InitiativeStatusName = (typeof INITIATIVE_STATUSES)[number];
export const INITIATIVE_STATUS_LABELS: Record<InitiativeStatusName, string> = {
  PLANNED: 'Planejada',
  IN_PROGRESS: 'Em andamento',
  AT_RISK: 'Em risco',
  DONE: 'Concluída',
  SUSPENDED: 'Suspensa',
  CANCELLED: 'Cancelada',
};
/** Status em que a iniciativa já terminou (não conta como atrasada). */
export const INITIATIVE_CLOSED_STATUSES: readonly InitiativeStatusName[] = ['DONE', 'CANCELLED'];

/** Como o progresso é obtido (T-090c): informado pela pessoa ou pelas ações concluídas dos planos da iniciativa. */
export const INITIATIVE_PROGRESS_MODES = ['MANUAL', 'ACTIONS'] as const;
export type InitiativeProgressModeName = (typeof INITIATIVE_PROGRESS_MODES)[number];
export const INITIATIVE_PROGRESS_MODE_LABELS: Record<InitiativeProgressModeName, string> = {
  MANUAL: 'Informado manualmente',
  ACTIONS: 'Pelas ações concluídas dos planos',
};

/**
 * Risco sem tratamento: sem ação vinculada, ou com a ação cancelada ou num plano arquivado (a ação não trata
 * mais o risco). Usado em "Requer atenção" (T-140).
 */
export const riskUntreated = (treatment: { status: string; planArchived: boolean } | null): boolean =>
  treatment === null || treatment.status === 'CANCELLED' || treatment.planArchived;

/** Probabilidade e impacto de um risco. */
export const RISK_LEVELS = ['LOW', 'MEDIUM', 'HIGH'] as const;
export type RiskLevelName = (typeof RISK_LEVELS)[number];
export const RISK_PROBABILITY_LABELS: Record<RiskLevelName, string> = {
  LOW: 'Baixa',
  MEDIUM: 'Média',
  HIGH: 'Alta',
};
export const RISK_IMPACT_LABELS: Record<RiskLevelName, string> = {
  LOW: 'Baixo',
  MEDIUM: 'Médio',
  HIGH: 'Alto',
};

/** Severidade do risco pela matriz probabilidade × impacto (1 a 3 cada): baixa, moderada, alta ou crítica. */
export const RISK_SEVERITIES = ['LOW', 'MODERATE', 'HIGH', 'CRITICAL'] as const;
export type RiskSeverityName = (typeof RISK_SEVERITIES)[number];
export const RISK_SEVERITY_LABELS: Record<RiskSeverityName, string> = {
  LOW: 'Baixa',
  MODERATE: 'Moderada',
  HIGH: 'Alta',
  CRITICAL: 'Crítica',
};
const RISK_SCORE: Record<RiskLevelName, number> = { LOW: 1, MEDIUM: 2, HIGH: 3 };
export function riskSeverity(probability: RiskLevelName, impact: RiskLevelName): RiskSeverityName {
  const score = RISK_SCORE[probability] * RISK_SCORE[impact];
  if (score >= 9) return 'CRITICAL';
  if (score >= 6) return 'HIGH';
  if (score >= 3) return 'MODERATE';
  return 'LOW';
}

/** Orçamento: saldo (previsto − realizado) e % consumido do previsto (null sem previsto). */
export function initiativeBudget(planned: number | null, actual: number | null) {
  return {
    balance: planned === null ? null : Math.round((planned - (actual ?? 0)) * 100) / 100,
    consumed: planned === null || planned === 0 ? null : Math.round(((actual ?? 0) / planned) * 10000) / 100,
  };
}

/** Atrasada: passou da data final sem estar concluída nem cancelada. */
export const initiativeLate = (
  i: { endDate: string | null; status: InitiativeStatusName },
  today: string,
): boolean => i.endDate !== null && i.endDate < today && !INITIATIVE_CLOSED_STATUSES.includes(i.status);

const ref = z.object({ id: z.string().uuid(), name: z.string() });

/** Ação de um plano da iniciativa (tratamento de risco e opções do formulário). */
export const initiativeActionRefSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  status: z.enum(ACTION_STATUSES),
  planId: z.string().uuid(),
  planName: z.string(),
  planArchived: z.boolean(),
});
export type InitiativeActionRef = z.infer<typeof initiativeActionRefSchema>;

export const initiativeRiskSchema = z.object({
  id: z.string().uuid(),
  description: z.string(),
  probability: z.enum(RISK_LEVELS),
  impact: z.enum(RISK_LEVELS),
  severity: z.enum(RISK_SEVERITIES),
  response: z.string().nullable(),
  /** Ação que trata o risco (de um plano desta iniciativa); null também quando o plano é de área fora do escopo. */
  treatment: initiativeActionRefSchema.nullable(),
  /** Há tratamento, mas num plano de área fora do escopo de quem vê: fica oculto e é mantido ao salvar (DEC-016). */
  treatmentHidden: z.boolean(),
  /** Sem ação de tratamento válida (ver riskUntreated). */
  untreated: z.boolean(),
});
export type InitiativeRisk = z.infer<typeof initiativeRiskSchema>;

/** Iniciativa com objetivo, perspectiva, área (a do objetivo), líder e equipe já resolvidos. */
export const initiativeSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  objective: ref.extend({ archived: z.boolean() }),
  perspective: ref,
  /** Área do objetivo: define quem enxerga a iniciativa no escopo por área (DEC-040). */
  department: ref.nullable(),
  leader: ref.nullable(),
  team: z.array(ref),
  budgetPlanned: z.number().nullable(),
  budgetActual: z.number().nullable(),
  /** Previsto − realizado (null sem previsto). */
  budgetBalance: z.number().nullable(),
  /** % do previsto já realizado (null sem previsto). */
  budgetConsumed: z.number().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  /** Progresso que vale: o informado (modo manual) ou o das ações concluídas (modo pelas ações). */
  progress: z.number(),
  progressMode: z.enum(INITIATIVE_PROGRESS_MODES),
  /** O valor informado à mão (guardado mesmo no modo pelas ações, para voltar a ele). */
  manualProgress: z.number(),
  /** Ações dos planos ativos da iniciativa: concluídas e as que contam (as não canceladas). */
  actions: z.object({ done: z.number().int(), counted: z.number().int() }),
  expectedImpact: z.string().nullable(),
  status: z.enum(INITIATIVE_STATUSES),
  /** Passou da data final sem estar concluída nem cancelada. */
  late: z.boolean(),
  risks: z.array(initiativeRiskSchema),
  /** Quantos riscos estão sem tratamento. */
  untreatedRisks: z.number().int(),
  notes: z.string().nullable(),
  /**
   * Item de diagnóstico transformado nesta iniciativa (T-160a; DEC-061). Título só para quem enxerga o item (módulo
   * Diagnóstico e escopo da área/unidade dele); para os demais, nulo.
   */
  diagnosis: z.object({ id: z.string().uuid(), title: z.string().nullable() }).nullable(),
  archivedAt: z.string().nullable(),
});
export type Initiative = z.infer<typeof initiativeSchema>;
export const initiativeListSchema = paginatedSchema(initiativeSchema);

export const initiativeListQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(100, 'Busca longa demais.').optional(),
  objectiveId: z.string().uuid().optional(),
  leaderId: z.string().uuid().optional(),
  perspectiveId: z.string().uuid().optional(),
  status: z.enum(INITIATIVE_STATUSES).optional(),
  /** true: só as que têm risco sem tratamento; false: só as que não têm nenhum. */
  untreatedRisks: z.enum(['true', 'false']).optional(),
  archived: z.enum(['true', 'false']).optional(),
});
export type InitiativeListQuery = z.input<typeof initiativeListQuerySchema>;

const optionalText = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `${label} pode ter no máximo ${max} caracteres.`)
    .nullable()
    .optional()
    .transform((v) => v || null);

/** Valor em reais (≥ 0, até duas casas). */
const money = z.coerce
  .number({ message: 'Informe o valor em números.' })
  .min(0, 'O valor não pode ser negativo.')
  .max(999_999_999_999.99, 'Valor alto demais.')
  .multipleOf(0.01, 'Use no máximo duas casas decimais.');

export const initiativeRiskInputSchema = z.object({
  /** Risco que já existia (mantém o id — a ação de tratamento se liga a ele); ausente = risco novo. */
  id: z.string().uuid().optional(),
  description: z
    .string({ message: 'Descreva o risco.' })
    .trim()
    .min(3, 'Descreva o risco.')
    .max(500, 'O risco pode ter no máximo 500 caracteres.'),
  probability: z.enum(RISK_LEVELS, { message: 'Escolha a probabilidade.' }),
  impact: z.enum(RISK_LEVELS, { message: 'Escolha o impacto.' }),
  response: optionalText(1000, 'A resposta planejada'),
  /** Ação de tratamento (de um plano desta iniciativa); null = sem tratamento. */
  treatmentActionId: z
    .string()
    .uuid('Ação inválida.')
    .nullable()
    .optional()
    .transform((v) => v ?? null),
});
export type InitiativeRiskInput = z.input<typeof initiativeRiskInputSchema>;

/** Formulário completo da iniciativa (criar e substituir com PUT — mande a iniciativa inteira). */
export const initiativeInputSchema = z
  .object({
    name: z.string().trim().min(3, 'Informe o nome da iniciativa.').max(160, 'Nome longo demais.'),
    description: optionalText(2000, 'A descrição'),
    objectiveId: z
      .string({ message: 'Escolha o objetivo relacionado.' })
      .uuid('Escolha o objetivo relacionado.'),
    leaderId: z
      .string()
      .uuid('Líder inválido.')
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    team: z
      .array(z.string().uuid('Pessoa inválida.'))
      .max(50, 'No máximo 50 pessoas na equipe.')
      .default([])
      .refine((ids) => new Set(ids).size === ids.length, 'Pessoa repetida na equipe.'),
    budgetPlanned: money
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    budgetActual: money
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    startDate: isoDate
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    endDate: isoDate
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    progress: z.coerce
      .number({ message: 'Informe o progresso em números.' })
      .min(0, 'O progresso vai de 0 a 100%.')
      .max(100, 'O progresso vai de 0 a 100%.')
      .multipleOf(0.01, 'Use no máximo duas casas decimais.')
      .default(0),
    progressMode: z.enum(INITIATIVE_PROGRESS_MODES).default('MANUAL'),
    expectedImpact: optionalText(2000, 'O impacto esperado'),
    status: z.enum(INITIATIVE_STATUSES).default('PLANNED'),
    risks: z
      .array(initiativeRiskInputSchema)
      .max(50, 'No máximo 50 riscos.')
      .default([])
      .refine(
        (risks) => {
          const ids = risks.flatMap((r) => (r.id ? [r.id] : []));
          return new Set(ids).size === ids.length;
        },
        { message: 'Risco repetido.' },
      ),
    notes: optionalText(2000, 'As observações'),
  })
  .refine((v) => !v.startDate || !v.endDate || v.startDate <= v.endDate, {
    message: 'A data final precisa ser igual ou posterior à inicial.',
    path: ['endDate'],
  });
export type InitiativeInput = z.input<typeof initiativeInputSchema>;

/** Opções do formulário e dos filtros: objetivos ativos no escopo, perspectivas e pessoas ativas. */
export const initiativeOptionsSchema = z.object({
  objectives: z.array(ref.extend({ perspectiveId: z.string().uuid() })),
  perspectives: z.array(ref),
  people: z.array(ref),
});
export type InitiativeOptions = z.infer<typeof initiativeOptionsSchema>;

/** Ações dos planos ativos da iniciativa (para escolher o tratamento dos riscos). */
export const initiativeActionsSchema = z.object({ items: z.array(initiativeActionRefSchema) });

/** Progresso pelas ações: concluídas sobre as não canceladas, em % (0 sem nenhuma que conte). */
export const actionsProgress = (a: { done: number; counted: number }): number =>
  a.counted === 0 ? 0 : Math.round((a.done / a.counted) * 10000) / 100;
